import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { $streemApiV1 } from "shared/boot/api";
import {
  normalizeContactList,
  type OutreachContactList,
  sortContactLists,
} from "shared/helpers/outreachContactLists";

export type { OutreachContactList };

const RECENT_CONTACT_LIST_LIMIT = 5;

interface LoadRecentContactListsOptions {
  organisationTeamId?: Array<number | string>;
  reload?: boolean;
  searchQuery?: string;
}

export const useOutreachContactListsStore = defineStore(
  "outreachContactLists",
  () => {
    const recentContactLists = ref<OutreachContactList[]>([]);
    const recentContactListsLoaded = ref(false);
    const recentContactListsLoading = ref(false);
    const recentContactListsLoadingTeamFilter = ref<string>();
    const recentContactListsTeamFilter = ref<string>();
    let latestRecentContactListsRequestId = 0;

    const visibleRecentContactLists = computed(() =>
      recentContactLists.value.slice(0, RECENT_CONTACT_LIST_LIMIT)
    );

    async function loadRecentContactLists({
      organisationTeamId,
      reload = false,
      searchQuery,
    }: LoadRecentContactListsOptions = {}) {
      const normalizedSearchQuery = searchQuery?.trim() ?? "";

      const requestKey = JSON.stringify({
        organisationTeamId,
        searchQuery: normalizedSearchQuery,
      });

      if (
        (recentContactListsLoaded.value &&
          recentContactListsTeamFilter.value === requestKey &&
          !reload) ||
        (recentContactListsLoading.value &&
          recentContactListsLoadingTeamFilter.value === requestKey &&
          !reload)
      ) {
        return;
      }

      latestRecentContactListsRequestId += 1;
      const requestId = latestRecentContactListsRequestId;
      recentContactListsLoading.value = true;
      recentContactListsLoadingTeamFilter.value = requestKey;

      try {
        const filter = {
          ...(organisationTeamId
            ? { organisation_team_id: organisationTeamId.join(",") }
            : {}),
          ...(normalizedSearchQuery
            ? { name: { match: normalizedSearchQuery } }
            : {}),
        };

        const { data: payload } = await $streemApiV1.get("contact_lists", {
          params: {
            ...(Object.keys(filter).length ? { filter } : {}),
            page: {
              number: 1,
              size: RECENT_CONTACT_LIST_LIMIT,
            },
            sort: "-updated_at",
          },
        });

        if (requestId === latestRecentContactListsRequestId) {
          recentContactLists.value = sortContactLists(
            payload.data.map(normalizeContactList)
          );

          recentContactListsLoaded.value = true;
          recentContactListsTeamFilter.value = requestKey;
        }
      } finally {
        if (requestId === latestRecentContactListsRequestId) {
          recentContactListsLoading.value = false;
        }
      }
    }

    function upsertContactList(contactList: OutreachContactList) {
      const contactLists = recentContactLists.value.filter(
        (existingContactList) => existingContactList.id !== contactList.id
      );

      recentContactLists.value = sortContactLists([
        contactList,
        ...contactLists,
      ]).slice(0, RECENT_CONTACT_LIST_LIMIT);

      recentContactListsLoaded.value = true;
    }

    return {
      loadRecentContactLists,
      normalizeContactList,
      recentContactLists,
      recentContactListsLoaded,
      recentContactListsLoading,
      upsertContactList,
      visibleRecentContactLists,
    };
  }
);

export default useOutreachContactListsStore;
